import type { ReactNode } from 'react';
import { Layout } from '@components/Layout';

const LAST_UPDATED = 'March 27, 2026';

const sectionClassName = 'rounded-xl border border-zinc-800 bg-zinc-900/70 p-6 shadow-lg';
const listClassName = 'mt-3 list-disc space-y-2 pl-5 text-zinc-300';

interface SectionProps {
    title: string;
    children: ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
    <section className={sectionClassName}>
        <h2 className="text-2xl font-semibold text-white">{title}</h2>
        <div className="mt-4 space-y-4 text-zinc-300">{children}</div>
    </section>
);

const TermsAndConditions = () => {
    return (
        <Layout title="Terms and Conditions - Project 314">
            <div className="mx-auto my-6 max-w-4xl px-4">
                <div className={sectionClassName}>
                    <p className="text-sm font-medium uppercase tracking-[0.2em] text-zinc-400">Project 314</p>
                    <h1 className="mt-2 text-4xl font-semibold text-white">Terms and Conditions</h1>
                    <p className="mt-3 text-sm text-zinc-400">Last updated: {LAST_UPDATED}</p>
                    <p className="mt-5 text-zinc-300">
                        These Terms and Conditions (&quot;Terms&quot;) govern your access to and use of Project 314 and any related
                        websites, applications, dashboards, brokerage integrations, APIs, AI features, automation tools, and other services
                        made available by Meteoric, LLC (&quot;Meteoric,&quot; &quot;we,&quot; &quot;us,&quot; and &quot;our&quot;)
                        (collectively, the &quot;Services&quot;).
                    </p>
                    <p className="mt-4 text-zinc-300">
                        The Services are software tools for creating, testing, monitoring, and operating trading-related workflows. By
                        accessing or using the Services, creating an account, connecting a brokerage, enabling a strategy, or submitting
                        instructions through the Services, you agree to these Terms. If you do not agree, do not access or use the Services.
                    </p>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                    <div className={sectionClassName}>
                        <h2 className="text-xl font-semibold text-white">Important Risk Notice</h2>
                        <ul className={listClassName}>
                            <li>
                                Project 314 is experimental software. Features may be incomplete, changed, suspended, or removed at any
                                time, with or without notice.
                            </li>
                            <li>
                                You assume all risk arising from your use of the Services, including bugs, outages, bad data, delayed
                                execution, failed orders, duplicate orders, stale quotes, missed signals, and trading losses.
                            </li>
                            <li>
                                Trading securities, options, and other financial instruments involves substantial risk, including the risk
                                of losing some or all of your capital.
                            </li>
                            <li>
                                You are solely responsible for deciding whether to use live trading, paper trading, manual approvals,
                                strategy settings, broker configurations, and AI-assisted workflows.
                            </li>
                        </ul>
                    </div>

                    <div className={sectionClassName}>
                        <h2 className="text-xl font-semibold text-white">No Advice</h2>
                        <ul className={listClassName}>
                            <li>
                                Unless we expressly agree otherwise in a separate written agreement signed by Meteoric, the Services do not
                                provide investment, legal, tax, accounting, or fiduciary advice.
                            </li>
                            <li>
                                Signals, backtests, rankings, scorecards, AI outputs, autopilot plans, prompts, and workflow suggestions are
                                informational tools only.
                            </li>
                            <li>
                                You must independently evaluate all information and monitor your own accounts, positions, orders, exposures,
                                and regulatory obligations.
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-6 space-y-6">
                    <Section title="1. Eligibility and Account Registration">
                        <p>You may use the Services only if:</p>
                        <ul className={listClassName}>
                            <li>You are at least 18 years old and legally capable of entering into a binding contract.</li>
                            <li>You are authorized to act for any person or entity whose accounts, credentials, or data you connect.</li>
                            <li>
                                Your use of the Services does not violate any law, regulation, contract, brokerage rule, or third-party
                                term.
                            </li>
                            <li>You provide accurate registration information and keep your credentials and security settings current.</li>
                        </ul>
                        <p>
                            You are responsible for all activity that occurs under your account, whether or not authorized by you, unless
                            applicable law provides otherwise.
                        </p>
                    </Section>

                    <Section title="2. Experimental Beta Services">
                        <p>
                            The Services are offered on an experimental, beta, and continuously evolving basis. You acknowledge and agree
                            that the Services may contain defects, vulnerabilities, design flaws, incomplete features, inaccurate outputs,
                            unexpected behavior, and integration failures.
                        </p>
                        <p>Without limiting the above, the Services may:</p>
                        <ul className={listClassName}>
                            <li>Fail to generate, send, receive, modify, or cancel an order or other brokerage instruction.</li>
                            <li>Generate duplicate, conflicting, or unintended orders, signals, alerts, or automation steps.</li>
                            <li>Use stale, delayed, incomplete, or incorrect market, account, or portfolio data.</li>
                            <li>Become unavailable during market hours or times of heightened volatility.</li>
                            <li>Behave differently in live trading than in backtests, simulations, or paper environments.</li>
                        </ul>
                        <p>
                            You accept these risks as a material part of these Terms and understand that you are effectively acting as a
                            beta tester when using the Services.
                        </p>
                    </Section>

                    <Section title="3. No Investment Advice; No Brokerage or Custody Relationship">
                        <p>
                            Unless explicitly stated in a separate written agreement signed by Meteoric, the Services are software and
                            informational tools only. Meteoric is not acting as your broker-dealer, exchange, clearing firm, custodian,
                            bank, attorney, accountant, tax adviser, or fiduciary.
                        </p>
                        <p>
                            Nothing in the Services constitutes a recommendation or solicitation to buy, sell, hold, exercise, or roll any
                            security, option, or financial instrument. You alone decide whether to trade, what to trade, when to trade, and
                            how much capital to risk.
                        </p>
                    </Section>

                    <Section title="4. Brokerage Connections, Credentials, and Authorizations">
                        <p>
                            The Services may allow you to connect brokerage accounts, link live or paper trading accounts, and use OAuth,
                            API keys, or other credentials to retrieve data or transmit instructions. By connecting an account or enabling a
                            workflow, you represent and warrant that you have all rights and authority needed to do so.
                        </p>
                        <p>You authorize us and our service providers to, as applicable:</p>
                        <ul className={listClassName}>
                            <li>Access, store, refresh, and use brokerage credentials, tokens, and connection metadata.</li>
                            <li>Retrieve account balances, buying power, positions, orders, fills, executions, and related data.</li>
                            <li>
                                Transmit requests, instructions, or data to connected brokerages in connection with the settings,
                                strategies, and workflows you configure.
                            </li>
                            <li>Maintain, troubleshoot, and re-establish integrations you have requested.</li>
                        </ul>
                        <p>
                            You remain solely responsible for the legality, suitability, and consequences of any instruction submitted
                            through the Services. We may refuse, delay, throttle, or stop any request at any time, but we have no obligation
                            to do so.
                        </p>
                    </Section>

                    <Section title="5. User Responsibilities and Operational Controls">
                        <p>You are solely responsible for:</p>
                        <ul className={listClassName}>
                            <li>
                                Configuring strategies, portfolios, trading profiles, limits, approvals, notifications, and account
                                mappings.
                            </li>
                            <li>
                                Maintaining sufficient capital, permissions, margin, options approval levels, and broker account status.
                            </li>
                            <li>
                                Monitoring open positions, cash, buying power, risk concentrations, pending orders, and account
                                restrictions.
                            </li>
                            <li>
                                Reviewing trade confirmations, broker statements, fills, corporate actions, fees, tax consequences, and
                                account history.
                            </li>
                            <li>
                                Using independent supervision, emergency procedures, and fallback methods if the Services become
                                unavailable.
                            </li>
                            <li>Promptly disconnecting or rotating credentials if you suspect compromise or unauthorized activity.</li>
                        </ul>
                        <p>
                            You must not rely on the Services as your sole risk control, sole means of account supervision, or sole channel
                            for managing live market exposure.
                        </p>
                    </Section>

                    <Section title="6. Trading Risk and Assumption of Loss">
                        <p>
                            You acknowledge that trading and investing involve substantial risk. Markets can move rapidly. Liquidity can
                            disappear. Quotes can gap. Orders can be partially filled, delayed, rejected, executed at worse prices than
                            expected, or remain open longer than intended.
                        </p>
                        <p>You assume all risk of loss and all other adverse consequences arising from or related to:</p>
                        <ul className={listClassName}>
                            <li>Any order, signal, rebalance, adjustment, close, cancel, replace, or other action taken or not taken.</li>
                            <li>Any bug, outage, latency, software defect, integration failure, data issue, or cyber event.</li>
                            <li>
                                Any use of live accounts, paper accounts, simulated portfolios, manual approvals, or automated workflows.
                            </li>
                            <li>
                                Any broker action or inaction, including rejection, throttling, disconnects, maintenance windows, or rule
                                enforcement.
                            </li>
                            <li>
                                Any market event, slippage, spread widening, volatility spike, halt, suspension, assignment, or expiration.
                            </li>
                        </ul>
                        <p>
                            You agree that Meteoric will not be responsible for your trading losses, lost profits, opportunity costs, tax
                            consequences, margin calls, liquidation events, or other financial harm.
                        </p>
                    </Section>

                    <Section title="7. Backtests, Simulations, Paper Trading, and Performance Information">
                        <p>
                            Backtests, experiments, scorecards, model outputs, hypothetical performance, and paper-trading results are
                            inherently limited. They are not actual trading results and are not guarantees of future performance.
                        </p>
                        <p>Such outputs may rely on assumptions that do not hold in live markets, including assumptions about:</p>
                        <ul className={listClassName}>
                            <li>
                                Liquidity, spreads, borrowing availability, slippage, routing, commissions, fees, and execution priority.
                            </li>
                            <li>
                                Survivorship, data cleanliness, look-ahead bias, corporate actions, timestamp alignment, and market impact.
                            </li>
                            <li>Continuous market access, stable APIs, and error-free strategy logic.</li>
                        </ul>
                        <p>
                            You agree not to interpret any backtest, paper result, or hypothetical metric as a promise, representation, or
                            assurance that similar results can or will be achieved in live trading.
                        </p>
                    </Section>

                    <Section title="8. AI and Autopilot Features">
                        <p>
                            The Services may include AI-assisted chat, drafting, analysis, planning, or autopilot-style workflows. AI
                            outputs may be incomplete, incorrect, outdated, misleading, biased, or entirely fabricated, and may
                            misunderstand your instructions or your account context.
                        </p>
                        <p>
                            You are solely responsible for reviewing, validating, approving, rejecting, editing, and supervising any AI
                            output before acting on it. AI outputs do not constitute investment advice or professional advice of any kind.
                        </p>
                        <p>
                            If you enable autonomous or semi-autonomous workflows, you assume the additional risk that the workflow may take
                            unintended actions, omit necessary actions, or act on incorrect assumptions.
                        </p>
                    </Section>

                    <Section title="9. Third-Party Services and Data">
                        <p>
                            The Services depend on third-party brokerages, market data sources, infrastructure vendors, authentication
                            providers, AI providers, and other services that we do not control. Those providers may change their systems,
                            terms, permissions, pricing, availability, response times, or policies at any time.
                        </p>
                        <p>
                            We are not responsible for third-party products, services, outages, API changes, data accuracy, trade execution,
                            custody, settlement, statements, fees, or security incidents. Broker or third-party books and records will
                            control in the event of any discrepancy with information shown in the Services.
                        </p>
                    </Section>

                    <Section title="10. Acceptable Use and Restrictions">
                        <p>You may not use the Services to:</p>
                        <ul className={listClassName}>
                            <li>
                                Violate any law, regulation, sanctions program, exchange rule, brokerage rule, or contractual restriction.
                            </li>
                            <li>
                                Engage in fraud, market manipulation, unauthorized trading, or activity on behalf of persons you are not
                                authorized to represent.
                            </li>
                            <li>
                                Interfere with the Services, bypass security controls, scrape data at scale, reverse engineer protected
                                systems, or test vulnerabilities without permission.
                            </li>
                            <li>
                                Use the Services in a way that could damage, disable, overburden, or impair the Services or any third-party
                                integration.
                            </li>
                            <li>
                                Misrepresent the Services or redistribute proprietary outputs, content, or software except as expressly
                                allowed.
                            </li>
                        </ul>
                    </Section>

                    <Section title="11. Intellectual Property and Limited License">
                        <p>
                            As between you and Meteoric, we and our licensors retain all right, title, and interest in and to the Services,
                            including all software, designs, interfaces, documentation, content, and related intellectual property rights.
                        </p>
                        <p>
                            Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable
                            license to access and use the Services for your internal, lawful use.
                        </p>
                        <p>
                            You retain ownership of content you submit to the Services, but you grant us a non-exclusive, worldwide license
                            to host, use, reproduce, modify, transmit, and process that content as needed to provide, secure, improve, and
                            support the Services.
                        </p>
                    </Section>

                    <Section title="12. Feedback">
                        <p>
                            If you provide suggestions, bug reports, ideas, or other feedback regarding the Services, you grant Meteoric a
                            perpetual, irrevocable, worldwide, royalty-free license to use and exploit that feedback without restriction or
                            compensation to you.
                        </p>
                    </Section>

                    <Section title="13. Suspension, Termination, and Changes">
                        <p>
                            We may modify, suspend, restrict, or discontinue any part of the Services at any time, with or without notice.
                            We may suspend or terminate your access if we believe you violated these Terms, created risk, exposed us or
                            others to liability, or if continuing to provide access is no longer operationally or legally feasible.
                        </p>
                        <p>
                            Termination or suspension will not excuse any obligations or liabilities that arose before the effective date of
                            termination, including those related to open disputes, indemnification, or outstanding legal obligations.
                        </p>
                    </Section>

                    <Section title="14. Warranty Disclaimer">
                        <p>
                            TO THE FULLEST EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED &quot;AS IS&quot; AND &quot;AS
                            AVAILABLE,&quot; WITH ALL FAULTS. METEORIC AND ITS AFFILIATES, LICENSORS, AND SERVICE PROVIDERS DISCLAIM ALL
                            WARRANTIES, EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY,
                            FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, QUIET ENJOYMENT, ACCURACY, AVAILABILITY, SECURITY, OR
                            THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS.
                        </p>
                    </Section>

                    <Section title="15. Limitation of Liability">
                        <p>
                            TO THE FULLEST EXTENT PERMITTED BY LAW, METEORIC AND ITS AFFILIATES, LICENSORS, SERVICE PROVIDERS, AND THEIR
                            RESPECTIVE OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                            CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, BUSINESS, DATA, GOODWILL,
                            TRADING OPPORTUNITY, OR CAPITAL, ARISING OUT OF OR RELATED TO THE SERVICES OR THESE TERMS, EVEN IF ADVISED OF
                            THE POSSIBILITY OF SUCH DAMAGES.
                        </p>
                        <p>
                            TO THE FULLEST EXTENT PERMITTED BY LAW, THE TOTAL AGGREGATE LIABILITY OF METEORIC AND ITS AFFILIATES, LICENSORS,
                            SERVICE PROVIDERS, AND THEIR RESPECTIVE OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS FOR ALL CLAIMS ARISING OUT OF
                            OR RELATED TO THE SERVICES OR THESE TERMS WILL NOT EXCEED THE GREATER OF ONE HUNDRED U.S. DOLLARS (US$100) OR
                            THE AMOUNT YOU PAID METEORIC FOR THE SERVICES DURING THE TWELVE MONTHS BEFORE THE EVENT GIVING RISE TO THE
                            CLAIM.
                        </p>
                        <p>
                            Nothing in these Terms is intended to waive, limit, or exclude any right or remedy that cannot lawfully be
                            waived, limited, or excluded under applicable law.
                        </p>
                    </Section>

                    <Section title="16. Indemnification">
                        <p>
                            You will defend, indemnify, and hold harmless Meteoric and its affiliates, licensors, service providers, and
                            their respective officers, directors, employees, and agents from and against any claims, demands, actions,
                            investigations, proceedings, liabilities, damages, losses, judgments, settlements, penalties, fines, costs, and
                            expenses, including reasonable attorneys&apos; fees, arising out of or related to:
                        </p>
                        <ul className={listClassName}>
                            <li>Your access to or use of the Services.</li>
                            <li>Your strategies, orders, instructions, data, content, or connected accounts.</li>
                            <li>Your violation of these Terms, applicable law, brokerage rules, or any third-party terms.</li>
                            <li>Your negligence, misconduct, fraud, or unauthorized use of the Services.</li>
                        </ul>
                    </Section>

                    <Section title="17. Governing Law and Venue">
                        <p>
                            These Terms and any dispute arising out of or relating to the Services or these Terms will be governed by the
                            laws of the State of Texas, without regard to its conflict of laws rules.
                        </p>
                        <p>
                            Subject to applicable law, you and Meteoric agree that any dispute not otherwise resolved between the parties
                            will be brought exclusively in the state or federal courts located in Travis County, Texas, and each party
                            consents to the personal jurisdiction and venue of those courts.
                        </p>
                    </Section>

                    <Section title="18. Changes to These Terms">
                        <p>
                            We may revise these Terms from time to time. When we do, we will post the updated version and update the
                            &quot;Last updated&quot; date above. By continuing to use the Services after updated Terms become effective, you
                            agree to the revised Terms.
                        </p>
                    </Section>

                    <Section title="19. Miscellaneous">
                        <p>
                            These Terms, together with any additional terms or policies we reference, form the entire agreement between you
                            and Meteoric regarding the Services, unless a separate written agreement signed by Meteoric says otherwise.
                        </p>
                        <p>
                            If any provision of these Terms is held unenforceable, the remaining provisions will remain in full force and
                            effect. Our failure to enforce any provision is not a waiver of that provision. You may not assign these Terms
                            without our prior written consent. We may assign these Terms in connection with a merger, acquisition,
                            reorganization, or sale of assets.
                        </p>
                    </Section>

                    <Section title="20. Contact Information">
                        <p>If you have questions about these Terms, contact us at:</p>
                        <address className="not-italic text-zinc-300">
                            Meteoric, LLC
                            <br />
                            701 Tillery St Ste 12 #B074
                            <br />
                            Austin, TX 78702
                            <br />
                            United States
                        </address>
                    </Section>
                </div>
            </div>
        </Layout>
    );
};

export default TermsAndConditions;
